import { useState } from "react";
import { supabase } from "../../supabaseClient";
import JSZip from "jszip";

const EVENT_ID = "66489b2f-1fa8-4307-a57d-d62a60dba217";

// ─── helpers ────────────────────────────────────────────────────────────────

const safeName = (brand) =>
  brand?.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "") || "unknown";

const signedUrl = async (path) => {
  const { data, error } = await supabase.storage
    .from("Global_Village_Vendors_2026")
    .createSignedUrl(path, 60 * 10); // 10-min window — enough for download
  if (error) throw new Error(`Signed URL failed for ${path}: ${error.message}`);
  return data.signedUrl;
};

const fetchBlob = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return res.blob();
};

// ─── component ──────────────────────────────────────────────────────────────

export default function VendorDocsDownloader() {
  const [status, setStatus]     = useState("idle"); // idle | loading | done | error
  const [log, setLog]           = useState([]);      // per-vendor status lines
  const [summary, setSummary]   = useState(null);    // { total, indemnity, agreement, missing }

  const pushLog = (line) => setLog((prev) => [...prev, line]);

  const handleDownload = async () => {
    setStatus("loading");
    setLog([]);
    setSummary(null);

    try {
      // 1. Pull all applications for this event that have at least one doc
      const { data: apps, error: appsErr } = await supabase
        .from("vendor_applications")
        .select(`
          id,
          indemnity_path,
          indemnity_name,
          agreement_path,
          agreement_name,
          vendors ( brand_name )
        `)
        .eq("event_id", EVENT_ID)
        .or("indemnity_path.not.is.null,agreement_path.not.is.null");

      if (appsErr) throw appsErr;
      if (!apps?.length) {
        pushLog("No documents found for this event.");
        setStatus("done");
        return;
      }

      const zip = new JSZip();
      const indemnityFolder  = zip.folder("indemnity");
      const agreementFolder  = zip.folder("agreement");

      let countIndemnity  = 0;
      let countAgreement  = 0;
      let countMissing    = 0;

      for (const app of apps) {
        const brand     = app.vendors?.brand_name || `vendor_${app.id}`;
        const brandSlug = safeName(brand);

        // ── indemnity ──
        if (app.indemnity_path) {
          try {
            const url  = await signedUrl(app.indemnity_path);
            const blob = await fetchBlob(url);
            const ext  = app.indemnity_name?.split(".").pop() || "pdf";
            indemnityFolder.file(`${brandSlug}_indemnity.${ext}`, blob);
            countIndemnity++;
            pushLog(`✅  ${brand} — indemnity`);
          } catch (err) {
            pushLog(`⚠️  ${brand} — indemnity FAILED: ${err.message}`);
            countMissing++;
          }
        } else {
          pushLog(`—   ${brand} — no indemnity on file`);
          countMissing++;
        }

        // ── agreement ──
        if (app.agreement_path) {
          try {
            const url  = await signedUrl(app.agreement_path);
            const blob = await fetchBlob(url);
            const ext  = app.agreement_name?.split(".").pop() || "pdf";
            agreementFolder.file(`${brandSlug}_agreement.${ext}`, blob);
            countAgreement++;
            pushLog(`✅  ${brand} — agreement`);
          } catch (err) {
            pushLog(`⚠️  ${brand} — agreement FAILED: ${err.message}`);
            countMissing++;
          }
        } else {
          pushLog(`—   ${brand} — no agreement on file`);
          countMissing++;
        }
      }

      // 2. Generate & trigger download
      pushLog("Compressing…");
      const blob = await zip.generateAsync({ type: "blob" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `vendor_docs_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      setSummary({
        total:     apps.length,
        indemnity: countIndemnity,
        agreement: countAgreement,
        missing:   countMissing,
      });
      setStatus("done");
    } catch (err) {
      pushLog(`❌ Fatal: ${err.message}`);
      setStatus("error");
    }
  };

  // ─── ui ───────────────────────────────────────────────────────────────────

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Vendor Documents</h2>
        <p style={styles.sub}>
          Downloads all indemnity &amp; agreement files sorted into two folders inside a ZIP.
        </p>

        <button
          style={{
            ...styles.btn,
            ...(status === "loading" ? styles.btnDisabled : {}),
          }}
          onClick={handleDownload}
          disabled={status === "loading"}
        >
          {status === "loading" ? "Preparing download…" : "⬇ Download All Documents"}
        </button>

        {summary && (
          <div style={styles.summary}>
            <span style={styles.pill}>{summary.total} vendors</span>
            <span style={{ ...styles.pill, ...styles.pillGreen }}>{summary.indemnity} indemnity</span>
            <span style={{ ...styles.pill, ...styles.pillGreen }}>{summary.agreement} agreements</span>
            {summary.missing > 0 && (
              <span style={{ ...styles.pill, ...styles.pillAmber }}>{summary.missing} missing</span>
            )}
          </div>
        )}

        {log.length > 0 && (
          <div style={styles.log}>
            {log.map((line, i) => (
              <div key={i} style={styles.logLine}>{line}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const styles = {
  wrap: {
    display: "flex",
    justifyContent: "center",
    padding: "2rem",
    fontFamily: "'Inter', sans-serif",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "2rem",
    width: "100%",
    maxWidth: "560px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  heading: {
    fontSize: "1.2rem",
    fontWeight: 600,
    margin: "0 0 0.25rem",
    color: "#111",
  },
  sub: {
    fontSize: "0.875rem",
    color: "#6b7280",
    margin: "0 0 1.5rem",
  },
  btn: {
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "0.65rem 1.25rem",
    fontSize: "0.9rem",
    fontWeight: 500,
    cursor: "pointer",
    width: "100%",
  },
  btnDisabled: {
    background: "#9ca3af",
    cursor: "not-allowed",
  },
  summary: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "1.25rem",
  },
  pill: {
    fontSize: "0.78rem",
    padding: "0.3rem 0.7rem",
    borderRadius: "999px",
    background: "#f3f4f6",
    color: "#374151",
    fontWeight: 500,
  },
  pillGreen: {
    background: "#dcfce7",
    color: "#166534",
  },
  pillAmber: {
    background: "#fef9c3",
    color: "#854d0e",
  },
  log: {
    marginTop: "1.25rem",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    maxHeight: "240px",
    overflowY: "auto",
  },
  logLine: {
    fontSize: "0.8rem",
    color: "#374151",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    lineHeight: 1.8,
  },
};